import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Download, Printer, Loader2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { AdminGate } from "../components/admin/AdminGate";
import { useAdminSession } from "../hooks/useAdminLeads";
import { fetchSeoPlan } from "../lib/api";

const download = (filename, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/markdown" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
};

const PlanBody = () => {
  const [doc, setDoc] = useState(null);
  useEffect(() => { fetchSeoPlan().then(setDoc).catch(() => setDoc({ error: true })); }, []);
  if (!doc) return <div className="py-20 flex justify-center" data-testid="seo-plan-loading"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;
  if (doc.error) return <p className="text-red-700" data-testid="seo-plan-error">Could not load the plan.</p>;
  return (
    <>
      <div className="flex flex-wrap gap-3 print:hidden">
        <button type="button" data-testid="seo-plan-download" onClick={() => download(doc.filename, doc.markdown)} className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-4 py-2 transition-colors">
          <Download className="w-4 h-4" /> Download (.md)
        </button>
        <button type="button" data-testid="seo-plan-print" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white text-slate-800 px-4 py-2 hover:border-cyan-500 transition-colors">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>
      <article data-testid="seo-plan-content" className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-10 prose prose-slate max-w-none prose-headings:text-slate-900 prose-a:text-cyan-700 prose-table:text-sm prose-th:bg-slate-50 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-slate-200">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.markdown}</ReactMarkdown>
      </article>
    </>
  );
};

export default function SeoPlan() {
  const { authed, login } = useAdminSession();
  return (
    <AdminGate page="adminSeoPlan" authed={authed} login={login}>
      <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <Seo page="adminSeoPlan" />
        <div className="max-w-4xl mx-auto" data-testid="seo-plan-page">
          <Link to="/admin" data-testid="seo-plan-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-cyan-700 print:hidden">
            <ArrowLeft className="w-4 h-4" /> Back to leads
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900 tracking-tight">SEO promotion plan</h1>
          <p className="mt-2 mb-6 text-base text-slate-600">90-day plan built around your press-release program. Download it, print it, or ask me to update it.</p>
          <PlanBody />
        </div>
      </div>
    </AdminGate>
  );
}
